import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useResponsive } from '../hooks/useResponsive';
import CountrySelector, { getDefaultCountry } from '../components/auth/CountrySelector';

export default function SignInPage() {
  const navigate = useNavigate();
  const { submitLoginContact, isLoading, error, clearError } = useAuth();
  const { isDesktop } = useResponsive();

  const [contact, setContact] = useState('');
  const [localError, setLocalError] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(getDefaultCountry);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    // Basic validation
    if (contact.length < 10) {
      setLocalError('Please enter a valid phone number');
      return;
    }

    try {
      // Prepend dial code if not already included
      let contactValue = contact;
      if (!contact.startsWith('+')) {
        contactValue = selectedCountry.dialCode + contact.replace(/^0+/, '');
      }

      // Check if contact exists in backend
      await submitLoginContact(contactValue, 'phone');
      // If successful, navigate to PIN page
      navigate('/pin');
    } catch (err) {
      // Error is displayed via context error state
      console.error('Login contact error:', err);
    }
  };

  const isValid = contact.length > 0;
  const displayError = localError || error;

  return (
    <div className="min-h-screen min-h-dvh flex flex-col bg-white">
      <div className={`flex-1 flex flex-col ${isDesktop ? 'max-w-md mx-auto w-full' : ''}`}>
        {/* Fixed Header - matches Flutter "Sign In" with back arrow */}
        <header className="fixed top-0 left-0 right-0 bg-white z-10 flex items-center gap-4 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] border-b border-gray-100">
          <div className={`flex items-center gap-4 ${isDesktop ? 'max-w-md mx-auto w-full' : ''}`}>
            <button
              onClick={() => navigate('/welcome')}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl font-bold">Sign In</h1>
          </div>
        </header>

        {/* Scrollable Content - with top padding for fixed header */}
        <div className="flex-1 overflow-y-auto px-6 py-8 pb-28 mt-[calc(3.5rem+env(safe-area-inset-top))]">
          {/* Centered Content Container - matches Flutter layout */}
          <div className="flex flex-col items-center pt-[6vh]">
            {/* Logo Icon - matches Flutter 72x72 */}
            <img
              src="/logo/bonndleLogoIcon_Indigo.svg"
              alt="Bonndle"
              className="w-[72px] h-[72px] mb-4"
            />

            {/* Welcome Back! - matches Flutter indigo color, 24px, bold */}
            <h2 className="text-2xl font-bold text-indigo-600 mb-3 text-center">
              Welcome Back!
            </h2>

            {/* Subtitle - centered, matches Flutter */}
            <p className="text-gray-600 mb-6 text-center">
              Enter your registered mobile number to proceed
            </p>

            {/* Error Display */}
            {displayError && (
              <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                {displayError}
              </div>
            )}

            <div className="w-full space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="flex gap-2">
                  <CountrySelector
                    selectedCountry={selectedCountry}
                    onSelectCountry={setSelectedCountry}
                    disabled={isLoading}
                  />
                  <input
                    type="tel"
                    value={contact}
                    onChange={(e) => {
                      setContact(e.target.value);
                      setLocalError('');
                    }}
                    placeholder="801 234 5678"
                    className="input flex-1"
                    autoFocus
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Bottom Button - matches Flutter 52px height, rounded-xl */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-6 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <div className={`${isDesktop ? 'max-w-md mx-auto' : ''}`}>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isValid || isLoading}
              className={`w-full h-[52px] rounded-xl font-bold text-lg transition-colors flex items-center justify-center gap-2 ${
                isValid && !isLoading
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                'Continue'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
